/**
 * Admin functions for managing site-wide images
 * Handles hero images, stock images, and exterior renders
 */
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

// ============================================================================
// Types
// ============================================================================

export interface StockImage {
  id: string;
  name: string;
  category: string;
  url: string;
  urls: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export interface CreateStockImageInput {
  name: string;
  category: string;
  url: string;
  urls?: Record<string, string>;
}

export interface UpdateStockImageInput {
  name?: string;
  category?: string;
  url?: string;
  urls?: Record<string, string>;
}

// ============================================================================
// Hero Images
// ============================================================================

/**
 * Get the primary hero image URL from site_config
 */
export async function getHeroImageUrl(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_image_url')
      .single();

    if (error) {
      console.error('Error fetching hero image URL:', error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error('Error in getHeroImageUrl:', error);
    return null;
  }
}

/**
 * Set the primary hero image URL
 */
export async function setHeroImageUrl(url: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_image_url',
        value: url,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero image URL:', error);
      return false;
    }

    trackEvent('hero_image_updated', { url });
    return true;
  } catch (error) {
    console.error('Error in setHeroImageUrl:', error);
    return false;
  }
}

/**
 * Get all hero images (array of URLs)
 */
export async function getHeroImages(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_images')
      .single();

    if (error) {
      console.error('Error fetching hero images:', error);
      return [];
    }

    try {
      const images = JSON.parse(data?.value || '[]');
      return Array.isArray(images) ? images : [];
    } catch (parseError) {
      console.error('Error parsing hero images JSON:', parseError);
      return [];
    }
  } catch (error) {
    console.error('Error in getHeroImages:', error);
    return [];
  }
}

/**
 * Add a hero image to the collection
 */
export async function addHeroImage(url: string): Promise<boolean> {
  try {
    const currentImages = await getHeroImages();
    const updatedImages = [...currentImages, url];

    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_images',
        value: JSON.stringify(updatedImages),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error adding hero image:', error);
      return false;
    }

    trackEvent('hero_image_added', { url, total: updatedImages.length });
    return true;
  } catch (error) {
    console.error('Error in addHeroImage:', error);
    return false;
  }
}

/**
 * Remove a hero image from the collection
 */
export async function removeHeroImage(url: string): Promise<boolean> {
  try {
    const currentImages = await getHeroImages();
    const updatedImages = currentImages.filter(img => img !== url);

    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_images',
        value: JSON.stringify(updatedImages),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error removing hero image:', error);
      return false;
    }

    trackEvent('hero_image_removed', { url, remaining: updatedImages.length });
    return true;
  } catch (error) {
    console.error('Error in removeHeroImage:', error);
    return false;
  }
}

// ============================================================================
// Hero Video Configuration
// ============================================================================

export type HeroMediaType = 'image' | 'video';
export type HeroMobileBehavior = 'static' | 'video';

export interface HeroVideoConfig {
  mediaType: HeroMediaType;
  videoUrlMp4: string | null;
  videoUrlWebm: string | null;
  posterUrl: string | null;
  mobileBehavior: HeroMobileBehavior;
}

/**
 * Get the hero media type (image or video)
 */
export async function getHeroMediaType(): Promise<HeroMediaType> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_media_type')
      .single();

    if (error) {
      console.error('Error fetching hero media type:', error);
      return 'image';
    }

    return (data?.value === 'video' ? 'video' : 'image') as HeroMediaType;
  } catch (error) {
    console.error('Error in getHeroMediaType:', error);
    return 'image';
  }
}

/**
 * Set the hero media type
 */
export async function setHeroMediaType(mediaType: HeroMediaType): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_media_type',
        value: mediaType,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero media type:', error);
      return false;
    }

    trackEvent('hero_media_type_updated', { mediaType });
    return true;
  } catch (error) {
    console.error('Error in setHeroMediaType:', error);
    return false;
  }
}

/**
 * Get the hero video URL (MP4)
 */
export async function getHeroVideoUrlMp4(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_video_url_mp4')
      .single();

    if (error) {
      console.error('Error fetching hero video URL (MP4):', error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error('Error in getHeroVideoUrlMp4:', error);
    return null;
  }
}

/**
 * Set the hero video URL (MP4)
 */
export async function setHeroVideoUrlMp4(url: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_video_url_mp4',
        value: url,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero video URL (MP4):', error);
      return false;
    }

    trackEvent('hero_video_mp4_updated', { url });
    return true;
  } catch (error) {
    console.error('Error in setHeroVideoUrlMp4:', error);
    return false;
  }
}

/**
 * Get the hero video URL (WebM)
 */
export async function getHeroVideoUrlWebm(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_video_url_webm')
      .single();

    if (error) {
      console.error('Error fetching hero video URL (WebM):', error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error('Error in getHeroVideoUrlWebm:', error);
    return null;
  }
}

/**
 * Set the hero video URL (WebM)
 */
export async function setHeroVideoUrlWebm(url: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_video_url_webm',
        value: url,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero video URL (WebM):', error);
      return false;
    }

    trackEvent('hero_video_webm_updated', { url });
    return true;
  } catch (error) {
    console.error('Error in setHeroVideoUrlWebm:', error);
    return false;
  }
}

/**
 * Get the hero video poster image URL
 */
export async function getHeroVideoPoster(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_video_poster')
      .single();

    if (error) {
      console.error('Error fetching hero video poster:', error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error('Error in getHeroVideoPoster:', error);
    return null;
  }
}

/**
 * Set the hero video poster image URL
 */
export async function setHeroVideoPoster(url: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_video_poster',
        value: url,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero video poster:', error);
      return false;
    }

    trackEvent('hero_video_poster_updated', { url });
    return true;
  } catch (error) {
    console.error('Error in setHeroVideoPoster:', error);
    return false;
  }
}

/**
 * Get the hero mobile behavior setting
 */
export async function getHeroMobileBehavior(): Promise<HeroMobileBehavior> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', 'hero_mobile_behavior')
      .single();

    if (error) {
      console.error('Error fetching hero mobile behavior:', error);
      return 'static';
    }

    return (data?.value === 'video' ? 'video' : 'static') as HeroMobileBehavior;
  } catch (error) {
    console.error('Error in getHeroMobileBehavior:', error);
    return 'static';
  }
}

/**
 * Set the hero mobile behavior setting
 */
export async function setHeroMobileBehavior(behavior: HeroMobileBehavior): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: 'hero_mobile_behavior',
        value: behavior,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error setting hero mobile behavior:', error);
      return false;
    }

    trackEvent('hero_mobile_behavior_updated', { behavior });
    return true;
  } catch (error) {
    console.error('Error in setHeroMobileBehavior:', error);
    return false;
  }
}

/**
 * Get all hero video configuration at once
 */
export async function getHeroVideoConfig(): Promise<HeroVideoConfig> {
  try {
    const [mediaType, videoUrlMp4, videoUrlWebm, posterUrl, mobileBehavior] = await Promise.all([
      getHeroMediaType(),
      getHeroVideoUrlMp4(),
      getHeroVideoUrlWebm(),
      getHeroVideoPoster(),
      getHeroMobileBehavior(),
    ]);

    return {
      mediaType,
      videoUrlMp4,
      videoUrlWebm,
      posterUrl,
      mobileBehavior,
    };
  } catch (error) {
    console.error('Error in getHeroVideoConfig:', error);
    return {
      mediaType: 'image',
      videoUrlMp4: null,
      videoUrlWebm: null,
      posterUrl: null,
      mobileBehavior: 'static',
    };
  }
}

/**
 * Update multiple hero video configuration values at once
 */
export async function updateHeroVideoConfig(config: Partial<HeroVideoConfig>): Promise<boolean> {
  try {
    const updates: Promise<boolean>[] = [];

    if (config.mediaType !== undefined) {
      updates.push(setHeroMediaType(config.mediaType));
    }
    if (config.videoUrlMp4 !== undefined) {
      updates.push(setHeroVideoUrlMp4(config.videoUrlMp4 || ''));
    }
    if (config.videoUrlWebm !== undefined) {
      updates.push(setHeroVideoUrlWebm(config.videoUrlWebm || ''));
    }
    if (config.posterUrl !== undefined) {
      updates.push(setHeroVideoPoster(config.posterUrl || ''));
    }
    if (config.mobileBehavior !== undefined) {
      updates.push(setHeroMobileBehavior(config.mobileBehavior));
    }

    const results = await Promise.all(updates);
    return results.every(result => result === true);
  } catch (error) {
    console.error('Error in updateHeroVideoConfig:', error);
    return false;
  }
}

// ============================================================================
// Stock Images
// ============================================================================

/**
 * Get stock images, optionally filtered by category
 */
export async function getStockImages(category?: string): Promise<StockImage[]> {
  try {
    let query = supabase
      .from('stock_images')
      .select('*')
      .order('created_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching stock images:', error);
      return [];
    }

    return (data || []).map(img => ({
      ...img,
      urls: img.urls as Record<string, string> | null,
    }));
  } catch (error) {
    console.error('Error in getStockImages:', error);
    return [];
  }
}

/**
 * Get a single stock image by ID
 */
export async function getStockImageById(id: string): Promise<StockImage | null> {
  try {
    const { data, error } = await supabase
      .from('stock_images')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching stock image:', error);
      return null;
    }

    return {
      ...data,
      urls: data.urls as Record<string, string> | null,
    };
  } catch (error) {
    console.error('Error in getStockImageById:', error);
    return null;
  }
}

/**
 * Create a new stock image
 */
export async function createStockImage(input: CreateStockImageInput): Promise<StockImage | null> {
  try {
    const { data, error } = await supabase
      .from('stock_images')
      .insert({
        name: input.name,
        category: input.category,
        url: input.url,
        urls: input.urls || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating stock image:', error);
      return null;
    }

    trackEvent('stock_image_created', { 
      id: data.id, 
      category: input.category,
      name: input.name 
    });

    return {
      ...data,
      urls: data.urls as Record<string, string> | null,
    };
  } catch (error) {
    console.error('Error in createStockImage:', error);
    return null;
  }
}

/**
 * Update a stock image
 */
export async function updateStockImage(
  id: string,
  input: UpdateStockImageInput
): Promise<StockImage | null> {
  try {
    const updateData: any = {};
    if (input.name !== undefined) updateData.name = input.name;
    if (input.category !== undefined) updateData.category = input.category;
    if (input.url !== undefined) updateData.url = input.url;
    if (input.urls !== undefined) updateData.urls = input.urls;

    const { data, error } = await supabase
      .from('stock_images')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating stock image:', error);
      return null;
    }

    trackEvent('stock_image_updated', { id });

    return {
      ...data,
      urls: data.urls as Record<string, string> | null,
    };
  } catch (error) {
    console.error('Error in updateStockImage:', error);
    return null;
  }
}

/**
 * Delete a stock image
 */
export async function deleteStockImage(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('stock_images')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting stock image:', error);
      return false;
    }

    trackEvent('stock_image_deleted', { id });
    return true;
  } catch (error) {
    console.error('Error in deleteStockImage:', error);
    return false;
  }
}

// ============================================================================
// Exterior Renders
// ============================================================================

/**
 * Get exterior render URL for a specific block
 */
export async function getExteriorRenderUrl(block: 'A' | 'B'): Promise<string | null> {
  try {
    const key = `exterior_render_block_${block.toLowerCase()}_url`;
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', key)
      .single();

    if (error) {
      console.error(`Error fetching exterior render URL for block ${block}:`, error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error(`Error in getExteriorRenderUrl for block ${block}:`, error);
    return null;
  }
}

/**
 * Set exterior render URL for a specific block
 */
export async function setExteriorRenderUrl(block: 'A' | 'B', url: string): Promise<boolean> {
  try {
    const key = `exterior_render_block_${block.toLowerCase()}_url`;
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key,
        value: url,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error(`Error setting exterior render URL for block ${block}:`, error);
      return false;
    }

    trackEvent('exterior_render_updated', { block, url });
    return true;
  } catch (error) {
    console.error(`Error in setExteriorRenderUrl for block ${block}:`, error);
    return false;
  }
}

/**
 * Get both exterior render URLs
 */
export async function getExteriorRenderUrls(): Promise<{ blockA: string | null; blockB: string | null }> {
  try {
    const [blockA, blockB] = await Promise.all([
      getExteriorRenderUrl('A'),
      getExteriorRenderUrl('B'),
    ]);

    return { blockA, blockB };
  } catch (error) {
    console.error('Error in getExteriorRenderUrls:', error);
    return { blockA: null, blockB: null };
  }
}

/** Block B view IDs: Northside, Southside, Center (tabbed views) */
export type BlockBViewId = 'northside' | 'southside' | 'center';

/** Section for Block B multi-view (one image per view) */
export interface ExteriorRenderBlockBSection {
  id: BlockBViewId;
  url: string;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
}

/** Input for setting Block B multi-image config */
export interface ExteriorRenderBlockBConfig {
  sections: ExteriorRenderBlockBSection[];
  totalDimensions: { width: number; height: number };
}

const BLOCK_B_LAYOUT_KEY = 'exterior_render_block_b_layout';

/**
 * Set Block B (Lagoon View) multi-image config. Pass sections and totalDimensions.
 * To revert to single image, set exterior_render_block_b_url and clear layout (set empty string).
 */
export async function setExteriorRenderBlockBConfig(
  config: ExteriorRenderBlockBConfig
): Promise<boolean> {
  try {
    const value = JSON.stringify({
      type: 'multi-image',
      arrangement: 'tabbed-views',
      sections: config.sections,
      totalDimensions: config.totalDimensions,
    });
    const { error } = await supabase
      .from('site_config')
      .upsert(
        {
          key: BLOCK_B_LAYOUT_KEY,
          value,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      );

    if (error) {
      console.error('Error setting Block B exterior render config:', error);
      return false;
    }

    trackEvent('exterior_render_config_updated', { block: 'B', type: 'multi-image' });
    return true;
  } catch (error) {
    console.error('Error in setExteriorRenderBlockBConfig:', error);
    return false;
  }
}

/**
 * Get Block B multi-image layout JSON string (for admin). Returns null if not set.
 */
export async function getExteriorRenderBlockBLayoutRaw(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', BLOCK_B_LAYOUT_KEY)
      .single();

    if (error || !data?.value) return null;
    return data.value;
  } catch {
    return null;
  }
}/**
 * Clear Block B multi-image config (revert to single image via exterior_render_block_b_url).
 */
export async function clearExteriorRenderBlockBConfig(): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert(
        {
          key: BLOCK_B_LAYOUT_KEY,
          value: '',
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      );    if (error) {
      console.error('Error clearing Block B layout:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error in clearExteriorRenderBlockBConfig:', error);
    return false;
  }
}
